/* eslint-disable valid-jsdoc */
import { Modal } from 'antd';
import Upload, { RcFile, UploadFile } from 'antd/es/upload';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserInfoV1 } from 'src/api/user';
import AppButton from 'src/components/base/Button/Button';
import { Config, UserServiceSection} from 'src/latest/src/constants/AppServerConfig';
import { saveResponses } from 'src/redux/actions/questionnaireActions';
import {
  deleteFile,
  updateUploadStatus,
  uploadDocumentWithPreSignedUrl,
} from 'src/redux/services/commonServices';
import { Logger } from 'src/latest/src/services';
import {
  blobToBase64,
  deepClone,
  formatUTCDate,
  prepareResponseData,
} from 'src/legacy/utils/helpers';
import './UploadDocument.scss';
import {
  UploadDocChoice,
  UploadDocPage,
  UploadDocQuestion,
} from 'src/latest/src/containers/QuestionnaireParent/components/UploadDocument/UploadDocumentType';
import { allowedFileTypes } from 'src/latest/src/components/Photo/constant';
import { getBase64 } from 'src/latest/src/utils/fileHelper';
import {
  UploadDocumentConstant,
  UploadFileSize,
} from 'src/latest/src/containers/QuestionnaireParent/components/UploadDocument/UploadDocumentConstant';
import { getAccessToken } from 'src/latest/src/auth/authStore';

interface UploadDocumentProps {
  className?: string;
  onClick?: () => void;
  isType?: boolean;
  placeholder?: string;
  onChange: (fileId: string | null, choiceId: string) => void;
  page?: UploadDocPage;
  question: UploadDocQuestion;
  isFileTypeInvalid: boolean;
  setIsFileTypeInvalid: (isInvalid: boolean) => void;
  description: string;
  choice: UploadDocChoice;
}

const UploadDocument: React.FC<UploadDocumentProps> = ({
  className,
  description,
  onClick,
  isType,
  placeholder,
  onChange,
  choice,
  page,
  question,
  isFileTypeInvalid,
  setIsFileTypeInvalid,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const USERS_SERVICE = Config?.userService;
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const currentQuestions = useSelector(
    (state: any) => state?.questionnaireData?.currentConfig?.currentQuestions,
  );
  const categoryId = useSelector(
    (state: any) =>
      state?.questionnaireData?.currentConfig?.currentCategory?.id,
  );
  const sessionId = useSelector(
    (state: any) =>
      state?.questionnaireData?.currentConfig?.questionnaireSessionId,
  );
  const userId = useSelector(
    (state: any) => state?.questionnaireData?.initialMetadata?.user_id,
  );
  const dispatch = useDispatch();

  /* download image and set to the setFileList array if value is present */
  const downloadImage = () => {
    if (choice?.metadata?.fileSignedUrl) {
      setIsButtonDisabled(true);
      setFileList([
        {
          uid: UploadDocumentConstant?.MINUS_ONE,
          name: UploadDocumentConstant?.IMAGE_NAME,
          status: UploadDocumentConstant?.DONE,
          url: decodeURIComponent(choice?.metadata?.fileSignedUrl),
        },
      ]);
    }
  };

  /* if value is present in choice then downolad image */
  useEffect(() => {
    if (choice?.value) {
      downloadImage();
    }
  }, []);

  /**
   * preview image
   * @param {*} file
   */
  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file?.originFileObj as RcFile);
    }

    setPreviewImage(file?.url || (file?.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file?.url?.substring(file?.url?.lastIndexOf('/') + 1) || '',
    );
  };

  /* modal close */
  const handleCancel = () => setPreviewOpen(false);

  /**
   * prevent upload used to prevent action call
   * @param {*} file
   * @returns
   */
  const beforeUpload = (file: RcFile) => {
    if (file.size > UploadFileSize) {
      setErrorMessage(UploadDocumentConstant?.ERROR_MSG_1);
      setIsFileTypeInvalid(true);

      return false;
    }

    if (!allowedFileTypes.includes(file.type)) {
      setErrorMessage(UploadDocumentConstant?.ERROR_MSG_2);
      setIsFileTypeInvalid(true);

      return false;
    }

    setIsFileTypeInvalid(false);

    return true;
  };

  /* Remove uploaded files and choice value null */
  const remove = async () => {
    setIsUploadingFile(true);

    if (choice.value) {
      await deleteFile(choice.value); // remove the file from backend
      if (page && page?.completed === true)
        updateQuestionValue(choice.unique_key, question?.unique_key);
    }

    setFileList([]);
    setIsButtonDisabled(false);
    onChange(null, choice?.id);
    setIsUploadingFile(false);
  };

  const updateQuestionValue = (choiceKey: string, questionKey: string) => {
    const questions = deepClone(currentQuestions);

    if (questions && questions.length !== 0) {
      let response = prepareResponseData(questions);

      response = response.map((r: any) => {
        if (r[questionKey]) {
          r[questionKey] = r[questionKey].map((c: any) => {
            if (c.unique_choice_key === choiceKey) c.value = null;

            return c;
          });
        }

        return r;
      });

      dispatch(
        saveResponses({
          sessionId,
          categoryId,
          pageId: page?.id,
          responses: response,
          hasChangedResponses: true,
          function() {
            Logger?.log(UploadDocumentConstant?.LOGGER_MSG_1);
          },
          updateConfig: false,
        }),
      );
    }
  };

  const boldItalicText = description.replace(
    /<b><i>(.*?)<\/i><\/b>/g,
    '<b><i>$1</i></b>',
  );

  const uploadProps = {
    accept: UploadDocumentConstant?.IMG_ACCEPT_PATH,
  };

  const customUploadRequest = async ({
    file,
    userId,
    onError,
  }: {
    file: RcFile;
    userId: string;
    onProgress?: (event: { percent: number }) => void;
    onSuccess?: (response: any) => void;
    onError?: (error: Error) => void;
  }) => {
    if (!file) {
      onError?.(new Error(UploadDocumentConstant?.ERROR_MSG_3));

      return;
    }

    const timestampMilliSeconds = new Date().getTime();
    const fileExtension = file.name.split('.').pop();
    const dateString = formatUTCDate(timestampMilliSeconds);

    if (isFileTypeInvalid) {
      remove();

      return;
    }

    if (
      choice?.metadata?.file_type?.length &&
      choice?.metadata?.file_name_prefix?.length
    ) {
      const base64Img = await blobToBase64(file);

      (file as UploadFile).thumbUrl = base64Img;
      const uploadFile = file as UploadFile;

      uploadFile.originFileObj = file;
      uploadFile.thumbUrl = base64Img;
      setFileList([uploadFile]);
      setIsUploadingFile(true);
      setIsButtonDisabled(true);
      setErrorMessage('');

      const accessToken = getAccessToken();

      const headers = new Headers({
        'Content-Type': UploadDocumentConstant?.APPLICATION_JSON,
        Authorization: `Bearer ${accessToken}`,
      });

      const options = {
        method: UploadDocumentConstant?.POST,
        headers,
        body: JSON.stringify({
          file_type: choice?.metadata?.file_type,
          mime_type: file.type,
          name: `${choice?.metadata?.file_name_prefix}${dateString}.${fileExtension}`,
          size_byte: file.size,
        }),
      };

      await fetchUserInfoV1(UserServiceSection?.profile_display, 'medium');

      try {
        const response = await fetch(
          `${
            Config.apiUrl + '/' + USERS_SERVICE
          }v1/document?user_id=${encodeURIComponent(userId)}`,
          options,
        );

        if (response.status === 200) {
          const responseUrl = await response.json();

          if (responseUrl.signed_url) {
            const uploadFinalStatus = await uploadDocumentWithPreSignedUrl(
              responseUrl.id,
              decodeURIComponent(responseUrl.signed_url),
              file,
            );

            if (
              UploadDocumentConstant?.DOCUMENT_ID in uploadFinalStatus &&
              UploadDocumentConstant?.FAILURE_REASON in uploadFinalStatus &&
              UploadDocumentConstant?.IS_UPLOADED in uploadFinalStatus
            ) {
              await updateUploadStatus(
                uploadFinalStatus.documentId,
                uploadFinalStatus.failureReason || '',
                uploadFinalStatus.isUploaded,
              );
            }
          }

          onChange(responseUrl?.id, choice?.id);
          setIsUploadingFile(false);
        } else if (response.status === 401) {
          setIsUploadingFile(false);
          Logger.log(UploadDocumentConstant?.LOGGER_MSG_2, response);

          // retry uploading
          if (userId) {
            customUploadRequest({ file, userId });
          }
        }
      } catch (error) {
        Logger.log(error);
        onError?.(error as Error);
        setIsUploadingFile(false);
      }
    }
  };

  function onUploadFile(obj: any) {
    const { file, onProgress, onSuccess, onError } = obj;

    if (userId) {
      customUploadRequest({ file, userId, onProgress, onSuccess, onError });
    }
  }

  return (
    <div className="p-2 upoload-insurance-main-container">
      <div
        className={`upload-insurance-wrapper text-center ${className} bg-${'light'} ${
          isType ? 'cursor-pointer' : ''
        }`}
        onClick={() => onClick?.()}
      >
        <div className="upload">
          <Upload
            listType="picture-card"
            fileList={fileList}
            customRequest={onUploadFile}
            maxCount={1}
            onPreview={handlePreview}
            id="upload-doc"
            className="test-upload"
            beforeUpload={beforeUpload}
            onRemove={remove}
            {...uploadProps}
          >
            {fileList?.length === 0 && (
              <div>
                <p
                  className="description"
                  dangerouslySetInnerHTML={{
                    __html: boldItalicText,
                  }}
                />
              </div>
            )}
          </Upload>
        </div>
        <Modal
          open={previewOpen}
          title={previewTitle}
          footer={null}
          onCancel={handleCancel}
        >
          <img alt="example" style={{ width: '100%' }} src={previewImage} />
        </Modal>
        <AppButton
          variant={'primary'}
          textSize="size-18"
          textWeight="bold"
          label={placeholder}
          onClick={() => document?.getElementById('upload-doc')?.click()}
          isProcessing={isUploadingFile}
          disabled={isUploadingFile || isButtonDisabled}
          className={'px-5 py-3 d-block mx-auto upload-doc-btn'}
        />
      </div>
      <p className="upload-insurance-wrapper-error__upload">{errorMessage}</p>
    </div>
  );
};

export default UploadDocument;
